import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "../assets/Loan/SingleLoanEmiReceipt.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";

const SingleLoanEmiReceipt = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loanAccount, emi, nextemi } = location.state || {};

  const receiptRef = useRef(null);
  const thermalReceiptRef = useRef(null);

  const { profile } = useSelector((state) => state.userProfile);
  const dispatch = useDispatch();
  const [company, setCompany] = useState(null);

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  const fetchCompany = async () => {
    try {
      const res = await fetch(
        `https://payyug.in/api/get-company?superAdminId=${profile?.superAdminId || 14}`,
      );
      const data = await res.json();
      if (res.ok && data) {
        setCompany({
          ...data,
          logoUrl: data.logo
            ? `https://payyug.in/${data.logo.replace(/^images\//, "")}`
            : null,
        });
      }
    } catch (err) {
      console.error("Error fetching company:", err);
    }
  };

  useEffect(() => {
    if (profile?.superAdminId) fetchCompany();
  }, [profile?.superAdminId]);

  // Calculations for Penalty logic
  const penalty = Number(emi?.penalty || emi?.penaltyAmount || 0);
  const baseEmi = Number(emi?.emiAmount || 0);
  const totalPaid = Number(emi?.amountPaid || baseEmi + penalty);

  // --- Original Print Logic (Unchanged) ---
  const handlePrint = async () => {
    if (!receiptRef.current) return;
    const pdf = new jsPDF("p", "mm", "a4");
    const canvas = await html2canvas(receiptRef.current, {
      scale: 1.5,
      useCORS: true,
      backgroundColor: "#fff",
    });
    const imgData = canvas.toDataURL("image/jpeg", 0.9);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;
    pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, imgHeight);
    const blobUrl = pdf.output("bloburl");
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = blobUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      window.addEventListener(
        "focus",
        () => {
          setTimeout(() => {
            if (document.body.contains(iframe))
              document.body.removeChild(iframe);
          }, 500);
        },
        { once: true },
      );
    };
  };

  const handlePrintThermalSlip = async () => {
    if (!thermalReceiptRef.current) return;
    try {
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(
        `<html><head><style>@page { size: 58mm auto; margin: 0; } body { margin: 0; padding: 0; display: flex; flex-direction: column; align-items: center; } img { width: 58mm; display: block; }</style></head><body></body></html>`,
      );
      doc.close();
      const canvas = await html2canvas(thermalReceiptRef.current, {
        scale: 3,
        backgroundColor: "#fff",
        useCORS: true,
      });
      const imgData = canvas.toDataURL("image/png");
      const img = doc.createElement("img");
      img.src = imgData;
      await new Promise((res) => (img.onload = res));
      doc.body.appendChild(img);
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      }, 300);
    } catch (err) {
      alert("Thermal print failed");
    }
  };

  if (!loanAccount || !emi)
    return <div className="p-10 text-center">Receipt Data Not Found</div>;

  return (
    <div className="receipt-viewer-container">
      <div className="receipt-actions receipt-no-print">
        <button onClick={() => navigate(-1)} className="receipt-back-btn">
          Back
        </button>
        <button onClick={handlePrint} className="btn-print-receipt">
          Print Receipt
        </button>
        <button
          onClick={handlePrintThermalSlip}
          className="btn-thermal-print-receipt"
        >
          Thermal Print Receipt
        </button>
      </div>

      <div id="receipt-0" className="receipt-paper" ref={receiptRef}>
        <div className="receipt-header-section">
          <div className="receipt-company-details">
            <img
              src={company?.logoUrl}
              alt="logo"
              className="receipt-logo"
              crossOrigin="anonymous"
            />
            <div className="receipt-header-company-info">
              <h1>{company?.name || "N/A"}</h1>
              <p>
                <b>Address: </b>
                {company?.address || "N/A"}
              </p>
              <p>
                <b>Phone:</b> {company?.contact || "N/A"} | <b>Email:</b>{" "}
                {company?.email || "N/A"}
              </p>
              <p>
                <b>CIN:</b> {company?.cin || "N/A"}
              </p>
            </div>
          </div>

          <div className="receipt-title-box">
            <h2>COLLECTION RECEIPT</h2>
            <p>
              Receipt No: #{emi?.id} | Date:{" "}
              {new Date(emi?.paymentDate || Date.now()).toLocaleDateString(
                "en-IN",
              )}
            </p>
          </div>
        </div>

        <div className="receipt-section">
          <h3 className="receipt-section-title">EMI & PENALTY DETAILS</h3>
          <div className="receipt-details-wrapper">
            <div className="receipt-grid-item">
              <span>Customer Name:</span>{" "}
              <strong>{loanAccount?.userName}</strong>
            </div>
            <div className="receipt-grid-item">
              <span>Loan Account Number:</span>{" "}
              <strong>{loanAccount?.loanAccountNumber}</strong>
            </div>
            <div className="receipt-grid-item">
              <span>EMI Number:</span> <strong>{emi?.emiNumber}</strong>
            </div>

            {/* Penalty Included in UI */}
            <div className="receipt-grid-item">
              <span>Base EMI Amount:</span>{" "}
              <strong>₹{baseEmi.toLocaleString("en-IN")}</strong>
            </div>
            {penalty > 0 && (
              <div className="receipt-grid-item penalty-row-highlight">
                <span>Penalty Charges:</span>{" "}
                <strong>₹{penalty.toLocaleString("en-IN")}</strong>
              </div>
            )}

            <div className="receipt-grid-item total-collection-row">
              <span>
                <b>Total Amount Paid:</b>
              </span>{" "}
              <strong>₹{totalPaid.toLocaleString("en-IN")}</strong>
            </div>

            <div className="receipt-grid-item">
              <span>Due Date:</span>{" "}
              <strong>
                {new Date(emi.dueDate).toLocaleDateString("en-IN")}
              </strong>
            </div>
            <div className="receipt-grid-item">
              <span>Paid Date:</span>{" "}
              <strong>
                {emi?.paymentDate
                  ? new Date(emi.paymentDate).toLocaleDateString("en-IN")
                  : new Date().toLocaleDateString("en-IN")}
              </strong>
            </div>
            <div className="receipt-grid-item">
              <span>Status:</span> <strong>{emi.status}</strong>
            </div>
            <div className="receipt-grid-item due-date-special">
              <span>Next Due Date:</span>{" "}
              <strong>
                {nextemi?.dueDate
                  ? new Date(nextemi.dueDate).toLocaleDateString("en-IN")
                  : "Completed"}
              </strong>
            </div>
          </div>
        </div>

        <div className="receipt-footer-signs">
          <div className="receipt-sign-box">
            <div className="receipt-sign-line"></div>
            <p>Authorized Signatory</p>
          </div>
          <div className="receipt-sign-box">
            <div className="receipt-sign-line"></div>
            <p>Customer Signature</p>
          </div>
        </div>

        <p className="receipt-system-gen-text">
          This is a system-generated slip and does not require a signature.
          <br />
          For any queries, please contact us during business hours.
        </p>
      </div>

      {/* Thermal Print Slip */}
      <div className="thermal-slip-wrapper">
        <div ref={thermalReceiptRef} className="thermal-slip">
          <div className="thermal-company">
            <div className="thermal-company-name">{company?.name}</div>
            <div className="thermal-company-text">{company?.address}</div>
            <div className="thermal-company-text">Ph: {company?.contact}</div>
          </div>
          <div className="thermal-title">EMI Collection</div>
          <div className="thermal-divider"></div>
          <div className="thermal-row">
            <b>Cust:</b> {loanAccount?.userName}
          </div>
          <div className="thermal-row">
            <b>EMI No:</b> {emi?.emiNumber}
          </div>
          <div className="thermal-row">Base EMI: ₹{baseEmi}</div>
          {penalty > 0 && (
            <div className="thermal-row">Penalty: ₹{penalty}</div>
          )}
          <div className="thermal-row">
            Due Date:{" "}
            {emi?.dueDate
              ? new Date(emi.dueDate).toLocaleDateString("en-IN")
              : "N/A"}
          </div>
          <div
            className="thermal-row"
            style={{ fontWeight: "bold", fontSize: "13px" }}
          >
            Total Paid: ₹{totalPaid}
          </div>
          <div className="thermal-row">
            Paid Date:{" "}
            {emi?.paymentDate
              ? new Date(emi.paymentDate).toLocaleDateString("en-IN")
              : new Date().toLocaleDateString("en-IN")}
          </div>
          <div
            className="thermal-row"
            style={{ fontWeight: "bold", fontSize: "13px" }}
          >
            Status: {emi?.status}
          </div>
          <div className="thermal-divider"></div>
          <div className="thermal-row">
            Next Due:{" "}
            {nextemi?.dueDate
              ? new Date(nextemi.dueDate).toLocaleDateString("en-IN")
              : "Completed"}
          </div>
          <div className="thermal-footer">
            <br />
            Thank You!
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleLoanEmiReceipt;
